/**
 * A strict JSON parser that reports where it stopped.
 *
 * `JSON.parse` words its errors differently in every browser and often gives only a
 * character offset. This one follows RFC 8259, keeps object keys in order (a `Map`),
 * keeps numbers exactly as written when JavaScript would change them, and says what it
 * expected and on which line and column. Typical mistakes — a trailing comma, a single
 * quote, a comment, an unquoted key — get their own message.
 */

import {
  DataError,
  type DataMap,
  type DataValue,
  type DataWarning,
  RawNumber,
} from "@/lib/tool-kit/latex-dev/data-model";

export interface JsonParseResult {
  value: DataValue;
  warnings: DataWarning[];
}

const MAX_DEPTH = 500;
const RE_NUMBER = /-?(?:0|[1-9][0-9]*)(?:\.[0-9]+)?(?:[eE][+-]?[0-9]+)?/y;
const RE_WORD = /[A-Za-z_$][A-Za-z0-9_$]*/y;
const RE_WORD_START = /[A-Za-z_$]/;
const RE_NUMBER_TAIL = /[0-9.eE]/;
const RE_HEX4 = /^[0-9a-fA-F]{4}$/;

const ESCAPES: Readonly<Record<string, string>> = {
  '"': '"',
  "\\": "\\",
  "/": "/",
  b: "\b",
  f: "\f",
  n: "\n",
  r: "\r",
  t: "\t",
};

const NON_JSON_LITERALS: ReadonlySet<string> = new Set([
  "NaN",
  "Infinity",
  "undefined",
]);

export function parseJson(src: string): JsonParseResult {
  const warnings: DataWarning[] = [];
  let i = 0;

  const position = (at: number): { line: number; column: number } => {
    let line = 1;
    let last = -1;
    for (let k = 0; k < at && k < src.length; k++) {
      if (src[k] === "\n") {
        line++;
        last = k;
      }
    }
    return { line, column: at - last };
  };

  const fail = (
    code: string,
    at = i,
    params: Record<string, string | number> = {}
  ): never => {
    const { line, column } = position(at);
    throw new DataError(code, line, column, params);
  };

  const skipSpace = () => {
    while (
      i < src.length &&
      (src[i] === " " || src[i] === "\t" || src[i] === "\n" || src[i] === "\r")
    ) {
      i++;
    }
  };

  const describeChar = (): string =>
    i < src.length ? String.fromCodePoint(src.codePointAt(i) ?? 0) : "";

  /** An error for a character that cannot start or continue a value here. */
  /** An error for a separator that should be here: the end of the text gets its own message. */
  const expected = (what: string): never =>
    i >= src.length
      ? fail("jsonUnexpectedEnd")
      : fail("jsonExpected", i, { expected: what });

  const unexpected = (): never => {
    if (i >= src.length) {
      return fail("jsonUnexpectedEnd");
    }
    const c = src[i];
    if (c === "'") {
      return fail("jsonSingleQuote");
    }
    if (c === "/" && (src[i + 1] === "/" || src[i + 1] === "*")) {
      return fail("jsonComment");
    }
    RE_WORD.lastIndex = i;
    const word = RE_WORD.exec(src);
    if (word && NON_JSON_LITERALS.has(word[0])) {
      return fail("jsonLiteral", i, { text: word[0] });
    }
    return fail("jsonUnexpectedToken", i, { char: describeChar() });
  };

  const readString = (): string => {
    const start = i;
    i++;
    let out = "";
    for (;;) {
      if (i >= src.length) {
        return fail("jsonUnterminatedString", start);
      }
      const c = src[i];
      if (c === '"') {
        i++;
        return out;
      }
      if (c < " ") {
        return fail("jsonControlChar");
      }
      if (c !== "\\") {
        out += c;
        i++;
      } else {
        out += readEscape();
      }
    }
  };

  const readEscape = (): string => {
    const e = src[i + 1];
    if (e === "u") {
      const hex = src.slice(i + 2, i + 6);
      if (!RE_HEX4.test(hex)) {
        return fail("jsonBadEscape", i, { char: "u" });
      }
      i += 6;
      return String.fromCharCode(Number.parseInt(hex, 16));
    }
    if (e === undefined || !Object.hasOwn(ESCAPES, e)) {
      return fail("jsonBadEscape", i, { char: e ?? "" });
    }
    i += 2;
    return ESCAPES[e];
  };

  const readNumber = (): DataValue => {
    RE_NUMBER.lastIndex = i;
    const match = RE_NUMBER.exec(src);
    if (!match) {
      return fail("jsonBadNumber");
    }
    const text = match[0];
    const after = src[i + text.length];
    if (after !== undefined && RE_NUMBER_TAIL.test(after)) {
      return fail("jsonBadNumber");
    }
    i += text.length;
    const n = Number(text);
    return String(n) === text && Number.isFinite(n) ? n : new RawNumber(text);
  };

  const readLiteral = (): DataValue => {
    RE_WORD.lastIndex = i;
    const word = RE_WORD.exec(src);
    const text = word ? word[0] : "";
    if (text === "true" || text === "false" || text === "null") {
      i += text.length;
      return text === "null" ? null : text === "true";
    }
    return unexpected();
  };

  const readList = (depth: number): DataValue[] => {
    i++;
    const list: DataValue[] = [];
    skipSpace();
    if (src[i] === "]") {
      i++;
      return list;
    }
    for (;;) {
      skipSpace();
      list.push(readValue(depth + 1));
      skipSpace();
      if (src[i] === ",") {
        const comma = i;
        i++;
        skipSpace();
        if (src[i] === "]") {
          fail("jsonTrailingComma", comma);
        }
      } else if (src[i] === "]") {
        i++;
        return list;
      } else {
        expected(", ]");
      }
    }
  };

  const readKey = (): string => {
    if (src[i] === '"') {
      return readString();
    }
    if (src[i] === "}") {
      return fail("jsonTrailingComma", i);
    }
    RE_WORD.lastIndex = i;
    if (RE_WORD.test(src) || src[i] === "'") {
      return src[i] === "'" ? fail("jsonSingleQuote") : fail("jsonUnquotedKey");
    }
    return unexpected();
  };

  const readObject = (depth: number): DataMap => {
    i++;
    const map: DataMap = new Map();
    skipSpace();
    if (src[i] === "}") {
      i++;
      return map;
    }
    for (;;) {
      skipSpace();
      const keyAt = i;
      const key = readKey();
      skipSpace();
      if (src[i] !== ":") {
        expected(":");
      }
      i++;
      skipSpace();
      const value = readValue(depth + 1);
      if (map.has(key)) {
        warnings.push({
          code: "duplicateKey",
          line: position(keyAt).line,
          params: { key },
        });
      }
      map.set(key, value);
      skipSpace();
      if (src[i] === ",") {
        i++;
      } else if (src[i] === "}") {
        i++;
        return map;
      } else {
        expected(", }");
      }
    }
  };

  const readValue = (depth: number): DataValue => {
    if (depth > MAX_DEPTH) {
      return fail("jsonTooDeep");
    }
    skipSpace();
    const c = src[i];
    if (c === "{") {
      return readObject(depth);
    }
    if (c === "[") {
      return readList(depth);
    }
    if (c === '"') {
      return readString();
    }
    if (c === "-" || (c >= "0" && c <= "9")) {
      return readNumber();
    }
    return c !== undefined && RE_WORD_START.test(c)
      ? readLiteral()
      : unexpected();
  };

  skipSpace();
  const value = readValue(0);
  skipSpace();
  if (i < src.length) {
    fail("jsonExtraData");
  }
  return { value, warnings };
}
